/* eslint-disable */
import { LoadingOutlined, PrinterOutlined } from '@ant-design/icons';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  Button,
  message,
  Space,
  Spin
} from 'antd';
import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from 'react-query';
import { useSearchParams } from 'react-router-dom';

import InfoCard from './InfoCard';
import TimeSheetTable from './TimeSheetTable';
import TimekeepingDetail from './Timekeeping';

import { useAppSelector } from 'app/store';
import HeaderPage from 'common/components/HeaderPage';
import PopupGuideSettingPopups from 'common/components/PopupGuideSettingPopups';
import { getLocalStorage, setLocalStorage } from 'common/services/common/storage';
import { exportAttendanceRecordsService } from 'common/services/extends/attendanceRecords';
import { exportAttendanceRecordService, getExportByCodeService } from 'common/services/extends/exports';
import { ExportAttendanceRecordRequestTypes } from 'common/services/extends/exports/types';
import { getPayrollPeriodsByIdService } from 'common/services/extends/payrollPeriods';
import {
  getProjectByIdService
} from 'common/services/extends/project';
import LOCAL_STORAGE from 'common/utils/constant';
import { projectSchema } from 'common/utils/schemas';
import roles, { getPermission } from 'configs/roles';
import { isPopupBlocked } from 'common/utils/functions';

interface ProjectForm {
  name: string;
  code: string;
  rangeDate: [string, string];
}

const defaultValues = {
  name: '',
  code: '',
};

const TimeKeepingDetail: React.FC<ActiveRoles> = () => {
  /* Hooks */
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const rolesUser = useAppSelector((state) => state.auth.roles);

  const projectId = searchParams.get('projectId') || '';
  const payrollPeriodId = searchParams.get('payrollPeriodId') || '';
  const queryKeyPayrollPeriod = ['attendance-records-detail', payrollPeriodId];

  /* States */
  const [downloading, setDownloading] = React.useState(false);
  const [isPopupBlock, setIsPopupBlock] = React.useState({
    status: false,
    link: ''
  });
  const [checkDownload, setCheckDownload] = React.useState({
    status: false,
    code: ''
  });

  /* ref */
  const clickedDownloadRef = React.useRef<boolean>(false);
  const checkStatusExportRef = React.useRef<boolean>(false);

  /* React-hook-form */
  const method = useForm<ProjectForm>({
    defaultValues,
    mode: 'onChange',
    resolver: yupResolver(projectSchema),
  });

  const queryKey = ['project-detail', projectId];

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { isLoading } = useQuery(
    queryKey,
    () => getProjectByIdService(projectId),
    {
      enabled: !!projectId,
      onSuccess: (data) => {
        if (data.projectData) {
          method.reset({
            name: data.projectData?.name || '',
            code: data.projectData?.code || '',
            rangeDate: [data.projectData?.fromDate, data.projectData?.toDate],
          });
        }
      },
    }
  );

  const { data: payrollData } = useQuery(
    ['payroll-periods-detail', payrollPeriodId],
    () => getPayrollPeriodsByIdService(payrollPeriodId),
    {
      enabled: !!payrollPeriodId && getPermission(rolesUser, roles.PAYROLL_PERIOD_INDEX),
    }
  );

  const { mutate: downloadAttendanceRecords, isLoading: downloadLoading } = useMutation(
    ['downloadAttendanceRecords'],
    () => exportAttendanceRecordsService({
      _payrollPeriodId: Number(payrollPeriodId)
    }),
    {
      onSuccess: (data) => {
        if (data?.link) {
          window.open(data.link);
          message.success(t('message.exportSuccess'));
        }
      },
      onError: () => {
        message.error(t('message.exportError'));
      }
    }
  );

  const {
    mutate: exportAttendanceRecordMutate,
  } = useMutation(
    ['exportAttendanceRecordMutate'],
    async (params: ExportAttendanceRecordRequestTypes) => exportAttendanceRecordService({
      payrollPeriodId: params.payrollPeriodId
    }),
    {
      onSuccess: async (data) => {
        if (data?.downloadUrl) {
          const blocked = getLocalStorage(LOCAL_STORAGE.BLOCKED_POPUPS);
          if (blocked) {
            setIsPopupBlock({
              status: true,
              link: data.downloadUrl
            });
            return;
          }
          message.success(t('message.exportSuccess'));
          setCheckDownload({
            status: false,
            code: ''
          });
          window.open(data.downloadUrl, '_blank');
        } else {
          setCheckDownload({
            status: true,
            code: data.code || ''
          });
        }
      },
      onError: () => {
        message.error(t('message.exportError'));
      }
    }
  );

  const onHandleDownloadTimeKeeping = async () => {
    try {
      if (clickedDownloadRef.current) {
        return;
      }
      clickedDownloadRef.current = true;

      setDownloading(true);
      setCheckDownload({
        status: false,
        code: ''
      });

      exportAttendanceRecordMutate({
        payrollPeriodId: Number(payrollPeriodId)
      });
    } catch (error) {
      message.error(t('message.exportError'));
      setDownloading(false);
      clickedDownloadRef.current = false;
      setCheckDownload({
        status: false,
        code: ''
      });
      setIsPopupBlock({
        status: false,
        link: ''
      });
    }
  }

  useEffect(() => {
    const blocked = isPopupBlocked('');
    if (blocked) {
      setLocalStorage(LOCAL_STORAGE.BLOCKED_POPUPS, 'true');
    } else {
      window.localStorage.removeItem(LOCAL_STORAGE.BLOCKED_POPUPS);
    }
  }, []);

  React.useEffect(() => {
    if (checkDownload.status && checkDownload.code) {
      let numberOfChecks = 0;
      const interval = setInterval(async () => {
        if (checkStatusExportRef.current) {
          console.log('Already checking status, skipping this interval.');
          return;
        }

        // Limit the number of checks to prevent infinite loops
        if (numberOfChecks >= 9) {
          clearInterval(interval);
          message.error(t('message.exportError'));
          setCheckDownload({
            status: false,
            code: ''
          });
          setDownloading(false);
          clickedDownloadRef.current = false;
          return;
        }
        console.log('Checking download status...');
        try {
          checkStatusExportRef.current = true;
          numberOfChecks += 1;
          const response = await getExportByCodeService(checkDownload.code);
          if (response?.status === 'failed') {
            clearInterval(interval);
            message.error(t('message.exportError'));
            setCheckDownload({
              status: false,
              code: ''
            });
            setIsPopupBlock({
              status: false,
              link: ''
            });
            setDownloading(false);
            clickedDownloadRef.current = false;
            return;
          }
          if (response?.downloadUrl && response.status === 'success') {
            const blocked = getLocalStorage(LOCAL_STORAGE.BLOCKED_POPUPS);
            if (blocked && response) {
              setIsPopupBlock({
                status: true,
                link: response?.downloadUrl
              });
              clearInterval(interval);
              setCheckDownload({
                status: false,
                code: ''
              });
              setDownloading(false);
              clickedDownloadRef.current = false;
              return;
            }
            clearInterval(interval);
            window.open(response.downloadUrl, '_blank');
            setCheckDownload({
              status: false,
              code: ''
            });
            setDownloading(false);
            clickedDownloadRef.current = false;
          } else {
            console.log('Download not ready yet, checking again...');
          }
        } catch (error) {
          clearInterval(interval);
          setIsPopupBlock({
            status: false,
            link: ''
          });
          console.error('Error checking download status:', error);
          message.error(t('message.exportError'));
          setCheckDownload({
            status: false,
            code: ''
          });
          setDownloading(false);
          clickedDownloadRef.current = false;
        } finally {
          checkStatusExportRef.current = false;
        }
      }, 3000); // Check every 3 seconds

      return () => clearInterval(interval); // Clear interval on unmount
    }
  }, [checkDownload, t]);

  /* Render */
  return (
    <Spin spinning={downloadLoading || isLoading}>
      <HeaderPage
        fixed
        title={t('sidebar.programTimekeeping')}
        rightHeader={(
          <Space>
            {getPermission(rolesUser, roles.ATTENDANCE_RECORD) && (
              <Button
                type="primary"
                icon={<PrinterOutlined />}
                onClick={onHandleDownloadTimeKeeping}
              >
                {
                  downloading ? (
                    <Space>
                      {t('exportReports.checkingDownload')}
                      <LoadingOutlined style={{ fontSize: 16 }} />
                    </Space>
                  ) : t('exportReports.downloadTimeKeepingReport')
                }
              </Button>
            )}
          </Space>
        )}
      />
      <div className="t-mainlayout_wrapper">
        <Space direction="vertical" style={{ width: '100%' }}>
          <InfoCard />
          <TimekeepingDetail
            endDate={payrollData?.payrollPeriodData?.endDate || ''}
            fromDate={payrollData?.payrollPeriodData?.startDate || ''}
            name={payrollData?.payrollPeriodData?.name || ''}
          />
          {
            getPermission(rolesUser, roles.ATTENDANCE_RECORD_INDEX) && (
              <TimeSheetTable
                monthPayroll={payrollData?.payrollPeriodData?.monthPayroll || ''}
                queryKey={queryKeyPayrollPeriod}
                onHandleDownload={() => downloadAttendanceRecords()}
                approved={payrollData?.payrollPeriodData?.supApproved}
              />
            )
          }
        </Space>
      </div>

      <PopupGuideSettingPopups
        isLoading={downloading}
        isPopupBlock={isPopupBlock}
        onClose={() => setIsPopupBlock({ status: false, link: '' })}
      />
    </Spin>
  );
};

export default TimeKeepingDetail;
